import { useCallback, useEffect, useState } from 'react';
import { HydratedVoicemail } from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { TagNotificationPayload } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { VoicemailTagsAlertEvent } from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { useQueryClient } from 'react-query';
import { useVoicemailTagNotification } from '@frontend/notifications';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useVoicemailDetailsPanelHelper } from './voicemail-hooks';

export const useWebsocketVoicemailTagEventSubscription = () => {
  const queryClient = useQueryClient();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isVmTagNotificationFeatureEnabled = getFeatureFlagValue('voicemail-tags-notification-feature-enabled');

  const {
    show: showPanel,
    setShow,
    context,
    panelType,
  } = useSlidePanelShallowStore('setShow', 'show', 'context', 'panelType');
  const { selectedLocationIds } = useAppScopeStore();
  const [pendingViewParams, setPendingViewParams] = useState<VoicemailTagsAlertEvent | null>(null);

  const { voicemailDetailsQuery, optimisticRemoveTag, optimisticApplyTag, updateReadStatusAsync, onDelete } =
    useVoicemailDetailsPanelHelper({
      params: pendingViewParams,
      selectedLocationIds,
      panelType,
      setShow,
    });

  useEffect(() => {
    const voicemailData = voicemailDetailsQuery.data;
    if (voicemailData) {
      const uniqueKey = `${voicemailData?.channelId}-${voicemailData?.mailboxId}-${voicemailData?.mediaId}-${voicemailData?.voicemailId}`;
      setShow(true, 'voicemailTranscription', {
        rows: [{ ...voicemailData }],
        currentRowId: uniqueKey || '',
        instanceId: null,
        toggleRowSelected: () => {},
        updateReadStatus: updateReadStatusAsync,
        deleteVoicemail: onDelete,
        voicemailsQueryKey: voicemailDetailsQuery.queryKey,
        optimisticApplyTag,
        optimisticRemoveTag,
        sectionFocus: showPanel ? (context as any)?.sectionFocus : 'transcript',
        userHasAccess: !!pendingViewParams?.userHasAccess,
        prioritizeQueryCache: true,
      });
    }
  }, [voicemailDetailsQuery.data, pendingViewParams]);

  const handleVmTagEventDetailsUpdate = ({
    voicemailMessageId,
    locationId,
    tagsPayload,
  }: {
    voicemailMessageId: string;
    locationId: string;
    tagsPayload: TagNotificationPayload[];
  }) => {
    const queryKeys = [selectedLocationIds, 'voicemail-event-panel-details', voicemailMessageId, locationId];
    const vmData = queryClient.getQueryData(queryKeys) as HydratedVoicemail;

    if (!vmData || (showPanel && panelType !== 'voicemailTranscription')) {
      return;
    }

    const assignedTagIds = tagsPayload.map((tag) => tag.id);
    const updatedTagIds = vmData?.tagIds?.length
      ? Array.from(new Set([...vmData.tagIds, ...assignedTagIds]))
      : assignedTagIds;
    queryClient.setQueryData(queryKeys, {
      ...vmData,
      tagIds: updatedTagIds,
    });
  };

  const { create: createNotification } = useVoicemailTagNotification({
    onView: async (event) => {
      const { payload: params } = event;
      if (pendingViewParams?.voicemailMessageId === params.voicemailMessageId) {
        voicemailDetailsQuery.refetch();
      } else {
        setPendingViewParams(params);
      }
    },
  });

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.VOICEMAIL_TAG>>(
    (event) => {
      const { params } = event;

      if (!isVmTagNotificationFeatureEnabled) {
        return;
      }

      // when view panel is already open, we need to update the call record data
      handleVmTagEventDetailsUpdate({
        voicemailMessageId: params.voicemailMessageId,
        locationId: params.locationId,
        tagsPayload: params.tagsNotificationPayload,
      });

      createNotification({
        id: event.id,
        timestamp: Date.now(),
        type: 'voicemail-tag',
        payload: params,
        state: {
          paused: false,
          status: 'unread',
        },
      });
    },
    [createNotification]
  );
  return useWebsocketEventSubscription(NotificationType.VOICEMAIL_TAG, eventHandler);
};
